import { Component, OnInit, ViewChild, OnDestroy, ViewContainerRef, ComponentRef } from '@angular/core';
import { LeasingService } from '../../core/services/leasing.service';
import { FindQuote } from '../../core/models/leasing/FindQuote';
import { ResponseBase } from '../../core/models/ResponseBase';
import { CommonModule } from '@angular/common';
import { ChargingComponent } from '../../shared/components/charging/charging.component';
import { DataService } from '../../core/services/data.service';
import { Subject, takeUntil, take, lastValueFrom } from 'rxjs';
import { ARRENDAMIENTOS_INMUEBLE, DESTINOS_INMUEBLE, TIPOS_INMUEBLE } from '../../core/models/PropertyTypes';
import { QuoteContact } from '../../core/models/leasing/QuoteContacts';
import { QuoteProperty } from '../../core/models/leasing/QuoteProperty';
import { firstValueFrom } from 'rxjs';
import { RoleTasksMap } from '../../core/models/TaskItem';
import { DigitalClientService } from '../../core/services/digital-client.service';
import { UploadDocumentComponent } from '../../shared/components/upload-document/upload-document.component';
import { LinkPersonJsonData } from '../../core/models/TenantLink';
import { ModalComponent } from '../../shared/components/modal/modal.component';
import { UtilsService } from '../../core/services/utils.service';
import { environment } from '../../../environments/environment';
import { TenantQuoteData, TenantQuoteObject } from '../../core/models/leasing/TenantQuoteData';
import { BusinessLogicService } from '../../core/services/business-logic.service';
import { ClsPersona } from '../../core/models/digital-client/ClsPersona';
import { LeasingForm } from '../../core/models/LeasingForm';
import { AddPropertiesVehiclesByRole } from '../../core/models/leasing/AddPropertiesVehiclesByRole';
import { ValidateForm } from '../../core/models/leasing/ValidateForm';
import { ConfirmModalComponent } from '../../shared/components/modalDynamic/confirm-modal.component';
import { ApiResponseValidateForm } from '../../core/models/leasing/ApiResponseValidateForm';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, ChargingComponent, UploadDocumentComponent, ModalComponent],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  tasksByRole: RoleTasksMap = {};
  private destroy$ = new Subject<void>();
  private pendingRequests: number = 0;
  private personDataCache = new Map<string, ClsPersona | null>();
  quoteData: FindQuote | null = null;
  propertyData: QuoteProperty | null = null;
  contactData: QuoteContact[] | null = null;
  tenantData: TenantQuoteData | TenantQuoteObject | null = null;
  leasingForms: LeasingForm[] = [];
  estados: ValidateForm[] = [];
  loginRole: number | null = null;
  isLinked: number | null = null;

  cantidadCodeudores: number = 0;
  codeudores: any[] = [];
  quotationNumber: number | null = null;
  isLoading = false;
  isAuth = false;

  isClosingBanner: boolean = false;

  get bannerClosed(): boolean {
    return this.dataService.isBannerClosed();
  }

  set bannerClosed(value: boolean) {
    this.dataService.setBannerClosed(value);
  }

  departments: { codigoDepartamento: number; nombreDepartamento: string; }[] = [];
  municipalities: { codigoMunicipio: number; nombreMunicipio: string; }[] = [];

  showUploadModal = false;
  selectedTaskTitle: string | null = '';
  selectedRoleName = '';
  selectedTaskId: number | null = 0;
  selectedRoleId: number | null = 0;

  @ViewChild(ChargingComponent, { static: true }) charging!: ChargingComponent;
  @ViewChild(ModalComponent) modal!: ModalComponent;
  private modalRef!: ComponentRef<ConfirmModalComponent>;

  constructor(
    private leasingService: LeasingService,
    private dataService: DataService,
    private digitalClientService: DigitalClientService,
    private utilsService: UtilsService,
    private businessLogicService: BusinessLogicService,
    private viewContainerRef: ViewContainerRef
  ) { }

  async ngOnInit() {
    this.loadLocations();

    this.dataService.quotationNumber$.pipe(take(1), takeUntil(this.destroy$)).subscribe(async quotationNumber => {
      if (quotationNumber) {
        this.quotationNumber = quotationNumber;

        this.quoteData = this.dataService.getQuoteData();
        this.propertyData = this.dataService.getQuotePropertyData();
        this.contactData = this.dataService.getQuoteContactData();
        this.tenantData = this.dataService.getTenantQuoteData();

        // Obtener loginRole
        this.loginRole = this.dataService.getLoginRole();
        if (this.loginRole === null) {
          const storedLoginRole = await this.dataService.getFromStorage(this.dataService.STORAGE_KEYS.LOGIN_ROLE);
          if (storedLoginRole !== null) {
            await this.dataService.setLoginRole(Number(storedLoginRole));
            this.loginRole = Number(storedLoginRole);
          }
        }

        // Obtener isLinked
        this.isLinked = this.dataService.getLinked();
        if (this.isLinked === null) {
          const storedLinked = await this.dataService.getFromStorage(this.dataService.STORAGE_KEYS.IS_LINKED);
          if (storedLinked !== null) {
            await this.dataService.setLinked(Number(storedLinked));
            this.isLinked = Number(storedLinked);
          }
        }

        //const hasDocumentsData = Object.keys(documentsRequired).length > 0;
        const isHomeLoaded = this.dataService.getHomeLoaded();

        if (!this.quoteData && !isHomeLoaded) {
          this.isLoading = true;
          this.charging.showSpinner();
          await this.businessLogicService.loadAndProcessLinkQuoteData(quotationNumber);
          await this.onFindQuote(quotationNumber);
          await this.onFindPropertyByQuoteHeader(quotationNumber);
          await this.onFindContactsByQuoteHeader(quotationNumber);
          await this.onLoadLeasingData();
          this.loadDocumentsData(quotationNumber);
          this.dataService.setHomeLoaded(true);
        } else if ((!this.propertyData || !this.contactData || !this.tenantData) && !isHomeLoaded) {
          this.charging.showSpinner();
          await this.businessLogicService.loadAndProcessLinkQuoteData(quotationNumber);
          await this.onFindPropertyByQuoteHeader(quotationNumber);
          await this.onFindContactsByQuoteHeader(quotationNumber);
          await this.onLoadLeasingData();
          this.cantidadCodeudores = this.quoteData!.inNumeroCodeudores || 0;
          this.generarCodeudores(this.cantidadCodeudores);
          this.loadDocumentsData(quotationNumber);
          this.dataService.setHomeLoaded(true);
        } else {
          this.cantidadCodeudores = this.quoteData!.inNumeroCodeudores || 0;
          this.generarCodeudores(this.cantidadCodeudores);
          await this.onLoadLeasingData();
          if (this.propertyData?.inCodPropertyDeparment && !this.municipalities.length) {
            this.loadMunicipalities(this.propertyData.inCodPropertyDeparment.toString());
          }

          this.tasksByRole = this.dataService.getRoleTasksMap();
        }
      }
    });
  }

  private checkAndHideSpinner(): void {
    this.pendingRequests--;
    if (this.pendingRequests <= 0) {
      this.isLoading = false;
      this.charging.hideSpinner();
    }
  }

  async onFindQuote(inIdQuoteHeader: number): Promise<void> {
    this.leasingService.FindQuote(inIdQuoteHeader).subscribe({
      next: async (response: ResponseBase<FindQuote>) => {
        if (response && response.value) {
          this.quoteData = response.value;
          this.cantidadCodeudores = response.value.inNumeroCodeudores || 0;
          this.generarCodeudores(this.cantidadCodeudores);
          this.dataService.updateFindQuoteData(response.value);
        }
        this.checkAndHideSpinner();
      },
      error: (err) => {
        console.error('Error FindQuote:', err);
        this.checkAndHideSpinner();
      },
    });
  }

  async onFindPropertyByQuoteHeader(inIdQuoteHeader: number): Promise<void> {
    this.leasingService.FindPropertyByQuoteHeader(inIdQuoteHeader).subscribe({
      next: async (response: ResponseBase<QuoteProperty>) => {
        if (response && response.value) {
          this.propertyData = response.value;
          this.dataService.updateQuotePropertyData(response.value);

          try {
            const [deptResponse, munResponse] = await Promise.all([
              firstValueFrom(this.digitalClientService.GetDepartamentosByPais()),
              firstValueFrom(this.digitalClientService.GetMunicipiosPorDepartamento(
                response.value.inCodPropertyDeparment.toString()
              ))
            ]);

            if (deptResponse?.value) {
              this.departments = deptResponse.value;
            }
            if (munResponse?.value) {
              this.municipalities = munResponse.value;
            }
          } catch (error) {
            console.error('Error loading locations:', error);
          }
        }
        this.checkAndHideSpinner();
      },
      error: (err) => {
        console.error('Error FindPropertyByQuoteHeader:', err);
        this.checkAndHideSpinner();
      }
    });
  }

  async onFindContactsByQuoteHeader(inIdQuoteHeader: number): Promise<void> {
    try {
      const response = await lastValueFrom(this.leasingService.FindContactsByQuoteHeader(inIdQuoteHeader));
      if (response && response.value) {
        this.contactData = response.value;
        this.dataService.updateQuoteContactData(response.value);
      }
    } catch (err) {
      console.error('Error FindContactsByQuoteHeader:', err);
    } finally {
      this.checkAndHideSpinner();
    }
  }

  // Este metodo carga en cache la informacion de ARR.TB_ValidateForms y FUCC (Si logra obtener el inIdPersona de FindUser (WCF))
  async onLoadLeasingData(): Promise<void> {
    const contacts = this.dataService.getQuoteContactData();
    if (!contacts) return;

    const roles = [1, 3, 4, 5];
    const response = await firstValueFrom(this.leasingService.getValidateFormsByQuotationNumber(this.quotationNumber!));
    const validatedRoles = response?.value?.map(form => form.idRole) || [];
    for (const role of roles) {
      const currentContact = contacts.find(contact => contact.inIdRole === role);
      const sended = response.value.find(form => form.idRole == role)?.isEmailSent!;
      const estado: ValidateForm | undefined = response.value.find(form => form.idRole === role);
      if (currentContact) {
        const persona = await this.fetchPersonData(
          currentContact.strPersonType,
          currentContact.inContactIdentification,
          currentContact.strContactFirstLastName
        );
        if (estado) {
          this.estados.push(estado);
        }
        if (persona) {
          const leasingForm: LeasingForm = {
            idRol: role,
            status: validatedRoles.includes(role),
            sended: sended,
            propertiesVehiclesByRole: {} as AddPropertiesVehiclesByRole,
            spouse: null,
            persona: persona
          };

          this.leasingForms.push(leasingForm);
        }
      }
    }
    this.dataService.updateLeasingFormData(this.leasingForms);
    this.dataService.updateValidateFormData(this.estados);
  }

  async fetchPersonData(tipoPerson: string, numeroDocumento: string, apellido: string): Promise<ClsPersona | null> {
    const cacheKey = `${tipoPerson}-${numeroDocumento}-${apellido}`;
    
    if (this.personDataCache.has(cacheKey)) {
      return this.personDataCache.get(cacheKey)!;
    }

    return new Promise((resolve) => {
      const tipo = tipoPerson === "N" ? "1" : "2";

      this.digitalClientService.FindUser(tipo, numeroDocumento, apellido).subscribe(user => {
        if (user?.value?.inIdPersona) {
          this.digitalClientService.getPersonData(user.value.inIdPersona).subscribe(personData => {
            const persona: ClsPersona = {
              datos_Basicos: personData.value.datos_Basicos,
              informacion_Financiera: personData.value.informacion_Financiera,
              informacion_Laboral: personData.value.informacion_Laboral,
              informacion_Persona_Publica: personData.value.informacion_Persona_Publica,
              informacion_Reclamaciones_En_Siniestros: personData.value.informacion_Reclamaciones_En_Siniestros,
              preguntas_PDP: personData.value.preguntas_PDP,
              vinculos_Poliza: personData.value.vinculos_Poliza || null,
              gobernabilidad: personData.value.gobernabilidad || []
            };
            this.personDataCache.set(cacheKey, persona);
            resolve(persona);
          });
        } else {
          this.personDataCache.set(cacheKey, null);
          resolve(null);
        }
      });
    });
  }


  generarCodeudores(cantidad: number): void {
    this.codeudores = Array(cantidad).fill({});
  }

  get filteredCodeudores(): any[] {
    if ((this.loginRole === 2 && this.isLinked === 0) || (this.loginRole === 1 && this.isLinked === 0)) {
      return this.codeudores;
    }
    const index = this.loginRole! - 3;
    return index >= 0 && index < this.codeudores.length ? [this.codeudores[index]] : [];
  }

  async onTaskClick(taskType: string, role: number | null = null, taskTitle: string | null = null, taskId: number | null = null, roleToLink: number | null = null): Promise<void> {
    if (taskType === 'operational') {
      await this.dataService.changeView('basic-data', role);
    } else if (taskType === 'document') {
      this.selectedTaskTitle = taskTitle;
      this.selectedRoleName = this.getRoleName(role);
      this.selectedTaskId = taskId;
      this.selectedRoleId = role || 0;
      this.showUploadModal = true;
    } else if (taskType === 'sendLink') {
      await this.sendLink(role!, roleToLink!);
    } else if (taskType === 'application-form') {
      await this.dataService.changeView('application-form', role);
    }
  }

  getRoleName(role: number | null): string {
    if (!role) return '';
    switch (role) {
      case 1:
        return 'Arrendatario';
      case 2:
        return 'Propietario';
      default:
        return `Codeudor ${role - 2}`;
    }
  }

  getPropertyType(value: number | undefined | null): string {
    return TIPOS_INMUEBLE.find(tipo => tipo.value === value)?.label ?? '';
  }

  getPropertyDestination(value: number | undefined | null): string {
    return DESTINOS_INMUEBLE.find(destino => destino.value === value)?.label ?? '';
  }

  getPropertyLeaseType(value: number | undefined | null): string {
    return ARRENDAMIENTOS_INMUEBLE.find(arr => arr.value === value)?.label ?? '';
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.personDataCache.clear();
  }

  async onDocumentSaved(): Promise<void> {
    const quotationNumber = this.dataService.getQuotationNumber();

    if (quotationNumber && this.selectedRoleId) {
      try {
        const response = await firstValueFrom(
          this.leasingService.FindQuoteDocumentsUploadedByIdRole(this.selectedRoleId, quotationNumber)
        );

        if (response?.value) {
          // Actualizar solo los documentos del rol específico
          this.dataService.updateDocumentsUploadedByRoleData(this.selectedRoleId, response.value);
          // Reconstruir el mapa de tareas
          this.tasksByRole = this.dataService.getRoleTasksMap();
        }
      } catch (error) {
        console.error('Error updating documents:', error);
      }
    }
  }

  private async loadDocumentsData(quotationNumber: number): Promise<void> {
    this.charging.showSpinner();
    try {
      // Cargar documentos subidos para roles principales
      await this.loadUploadedDocuments(quotationNumber, [1, 2]);

      // Cargar documentos para codeudores si existen
      if (this.quoteData?.inNumeroCodeudores) {
        // Para documentos subidos, cargamos para cada codeudor
        const codeudorRoles = Array.from(
          { length: this.quoteData.inNumeroCodeudores },
          (_, i) => i + 3
        );
        await this.loadUploadedDocuments(quotationNumber, codeudorRoles);
      }

      // Cargar documentos requeridos basados en contactData
      if (this.contactData) {
        const processedRoles = new Set<number>();

        for (const contact of this.contactData) {
          const roleToUse = contact.inIdRole;// > 3 ? contact.inIdRole + 1 : contact.inIdRole;
          // Solo cargar si no hemos procesado este rol antes
          if (!processedRoles.has(roleToUse)) {
            const idResponse = contact.strPersonType === 'N' && roleToUse != 2 ? 1 : 0
            const response = await firstValueFrom(
              this.leasingService.FindDocumentsRequiredByTypePersonByRole(
                contact.strPersonType,
                roleToUse,
                idResponse
              )
            );

            if (response?.value) {
              // Para roles > 3, guardamos la data con el rol original pero usando la respuesta del rol 3
              this.dataService.updateDocumentsRequiredByRoleData(
                contact.inIdRole,
                response.value
              );
            }

            processedRoles.add(roleToUse);
          }
        }

        // Construir tasksByRole después de cargar todos los documentos
        this.tasksByRole = this.dataService.getRoleTasksMap();
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      this.charging.hideSpinner();
    }
  }

  private async loadUploadedDocuments(quotationNumber: number, roles: number[]): Promise<void> {
    for (const role of roles) {
      try {
        const response = await firstValueFrom(
          this.leasingService.FindQuoteDocumentsUploadedByIdRole(role, quotationNumber)
        );

        if (response?.value) {
          this.dataService.updateDocumentsUploadedByRoleData(role, response.value);
        }
      } catch (error) {
        console.error(`Error loading documents for role ${role}:`, error);
      }
    }
  }

  isContactInfoAvailable(role: number): boolean {
    return this.contactData?.some(contact => {
      // Para codeudores (role >= 3), verificar si hay algún contacto con ese rol específico
      if (role >= 3) {
        return contact.inIdRole === role;
      }
      // Para propietario y arrendatario, verificar sus roles específicos
      return contact.inIdRole === role;
    }) ?? false;
  }

  isTenantInfoMainRole(roleId: number): boolean {
    const tenantData = this.dataService.getTenantQuoteData(roleId) as TenantQuoteObject;
    if (!tenantData) return false;
    
    const hasContactInfo = this.contactData?.some(contact => contact.inIdRole === roleId) ?? false;
    return (tenantData.rolprincipal ?? false) && hasContactInfo;
  }

  getCompletedTaskCount(role: number): string {
    const completedCount = this.isContactInfoAvailable(role) ? 1 : 0;
    const totalRequiredTasks = this.getTotalRequiredTasks(role);
    return `${completedCount}/${totalRequiredTasks} Tareas`;
  }

  private getTotalRequiredTasks(role: number): number {
    switch (role) {
      case 1: // Arrendatario
        return 6; // Ajusta este número según las tareas requeridas para arrendatario
      case 2: // Propietario
        return 3; // Ajusta este número según las tareas requeridas para propietario
      default: // Codeudor
        return 7; // Ajusta este número según las tareas requeridas para codeudor
    }
  }

  getRequiredTaskCount(role: number): number {
    return this.tasksByRole[role]?.filter(task => task.required).length ?? 0;
  }

  getCompletedRequiredTaskCount(role: number): number {
    return this.tasksByRole[role]?.filter(task => task.required && task.completed).length ?? 0;
  }

  getProgressPercentage(role: number): number {
    const requiredTasks = this.getRequiredTaskCount(role);
    const completedRequired = this.getCompletedRequiredTaskCount(role);
    return requiredTasks > 0 ? Math.floor((completedRequired / requiredTasks) * 100) : 0;
  }

  private async loadLocations(): Promise<void> {
    try {
      const deptResponse = await firstValueFrom(this.digitalClientService.GetDepartamentosByPais());
      if (deptResponse?.value) {
        this.departments = deptResponse.value;

        if (this.propertyData?.inCodPropertyDeparment) {
          await this.loadMunicipalities(this.propertyData.inCodPropertyDeparment.toString());
        }
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  }

  private async loadMunicipalities(departmentId: string): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.digitalClientService.GetMunicipiosPorDepartamento(departmentId)
      );
      if (response?.value) {
        this.municipalities = response.value;
      }
    } catch (error) {
      console.error('Error loading municipalities:', error);
    }
  }

  getDepartmentName(departmentId: number): string {
    const department = this.departments.find(d => d.codigoDepartamento === departmentId);
    return department?.nombreDepartamento || '';
  }

  getMunicipalityName(municipalityId: number): string {
    const municipality = this.municipalities.find(m => m.codigoMunicipio === municipalityId);
    return municipality?.nombreMunicipio || '';
  }

  async sendLink(roleId: number, roleToSendLink: number): Promise<void> {
    const quotationNumber = this.dataService.getQuotationNumber();
    if (!quotationNumber) return;

    try {
      this.charging.showSpinner();

      const linkResponse = await firstValueFrom(
        this.leasingService.FindLinkQuotePerson(quotationNumber, roleId)
      );

      if (!linkResponse?.value?.strJson) {
        throw new Error('No link data found');
      }

      let email = '';
      let phone = '';
      let roleLinkedName = '';

      if (roleId === 2 && roleToSendLink === 1) {
        // Propietario vinculando arrendatario
        const jsonData: LinkPersonJsonData = JSON.parse(linkResponse.value.strJson);
        email = jsonData.f0?.strEmail_v || '';
        phone = jsonData.f0?.strCelular_v || '';
        roleLinkedName = 'Arrendatario';
      } else if (roleId === 1 && roleToSendLink === 2) {
        // Arrendatario vinculando propietario
        const jsonData: LinkPersonJsonData = JSON.parse(linkResponse.value.strJson);
        email = jsonData.f0?.strEmail_v || '';
        phone = jsonData.f0?.strCelular_v || '';
        roleLinkedName = 'Propietario';
      } else {
        // Arrendatario vinculando codeudores
        const jsonData: LinkPersonJsonData = JSON.parse(linkResponse.value.strJson);

        const codeudorIndex = roleToSendLink - 3;

        if (jsonData.f0?.vinculado && jsonData.f0.vinculado[codeudorIndex]) {
          email = jsonData.f0.vinculado[codeudorIndex].strEmail || '';
          phone = jsonData.f0.vinculado[codeudorIndex].strCelular || '';
        }
        roleLinkedName = 'Codeudor';
      }

      if (!email || !phone) {
        throw new Error('Invalid link data');
      }

      // Generar el link encriptado
      //const targetRole = this.loginRole === 2 ? 1 : roleId;
      const encrypted = await this.utilsService.encryptUrlParams(quotationNumber, roleToSendLink);
      const baseUrl = encodeURIComponent(environment.SoliUrl);
      const encodedHash = encodeURIComponent(encrypted);
      const fullLink = `${baseUrl}/%23/${encodedHash}`;  // Usamos %23 para # codificado

      // Enviar el link
      const response = await firstValueFrom(
        this.leasingService.SendLinkPersonEmail(
          parseInt(phone),
          email,
          roleId,
          fullLink,
          quotationNumber,
          roleLinkedName
        )
      );

      if (response.status) {
        this.modal.openModal(
          'Envio de link vinculo',
          `Se ha enviado link de vinculación del proceso al Correo Electrónico ${this.utilsService.obfuscateEmail(email)}`,
          true
        );
      }
    } catch (error) {
      console.error('Error sending link:', error);
      this.modal.openModal(
        'Error',
        'Ocurrió un error al enviar el link de vinculación',
        false
      );
    } finally {
      this.charging.hideSpinner();
    }
  }

  async closeBanner() {
    this.isClosingBanner = true;
    await new Promise(resolve => setTimeout(resolve, 500));
    this.bannerClosed = true;
    this.isClosingBanner = false;
  }

  async onStartSigningProcess(event: Event, role: number): Promise<void> {
    event.stopPropagation();
    const response = await firstValueFrom(this.leasingService.getValidateFormsByQuotationNumber(this.quotationNumber!));
    this.viewContainerRef.clear();
    this.modalRef = this.viewContainerRef.createComponent(ConfirmModalComponent);

    if (response.value.some(sign => sign.idRole == role && sign.isEmailSent == true)) {
      this.modalRef.instance.openModal(
        'Reenvío de documento para firma electrónica',
        'Anteriormente se envió al correo el documento para el proceso de firma electrónica. ¿Desea volver a enviarlo?',
        'Sí, continuar',
        'Cancelar',
        () => this.handleAccept(role),
        () => this.handleCancel()
      );
    }
    else {
      this.modalRef.instance.openModal(
        'Se iniciará el proceso de firma electrónica',
        'Se generará el documento para la firma electrónica y recibirá un email donde podrá continuar con el proceso.',
        'Sí, continuar',
        'Cancelar',
        () => this.handleAccept(role),
        () => this.handleCancel()
      );
    }
  }

  async handleAccept(role: number) {
    try {
      this.charging.showSpinner("Generando documento para firma electrónica...");
      await this.businessLogicService.sendSign(role);
      this.modal.openModal(
        'El documento fue generado exitosamente.',
        'Recibirá un email donde podrá continuar con el proceso de firma electrónica.',
        true
      );
    } catch (error) {
      this.modal.openModal('Ha ocurrido un error', 'Ha ocurrido un error en el proceso de la generacion del documento para la firma electrónica', false);
    } finally {
      this.charging.hideSpinner();
    }
  }

  async handleCancel() {
  }
}